/**
 * Working-bin store: per-project list of saved archive clips, with debounced
 * autosave (1.5s after the last change, via PATCH /api/projects/{id}).
 */
import { create } from 'zustand';
import { backend, type BinEntry } from '../api/backend';
import { genId } from '../format/helpers';
import type { SaveState } from './project';

const AUTOSAVE_MS = 1500;

interface BinState {
  projectId: number | null;
  entries: BinEntry[];
  saveState: SaveState;
  load(projectId: number, entries: BinEntry[]): void;
  unload(): void;
  /** Add an entry; generates id + addedAt, returns the new id. */
  add(entry: Omit<BinEntry, 'id' | 'addedAt'>): string;
  update(id: string, patch: Partial<Omit<BinEntry, 'id'>>): void;
  remove(id: string): void;
  reorder(id: string, toIndex: number): void;
  saveNow(): Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

export const useBin = create<BinState>((set, get) => ({
  projectId: null,
  entries: [],
  saveState: 'saved',

  load(projectId, entries) {
    get().unload();
    set({ projectId, entries, saveState: 'saved' });
  },

  unload() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    set({ projectId: null, entries: [], saveState: 'saved' });
  },

  add(entry) {
    const id = genId('bin_');
    const full: BinEntry = { ...entry, id, addedAt: new Date().toISOString() };
    set((s) => ({ entries: [...s.entries, full] }));
    markDirty();
    return id;
  },

  update(id, patch) {
    set((s) => ({
      entries: s.entries.map((e) => (e.id === id ? { ...e, ...patch, id } : e)),
    }));
    markDirty();
  },

  remove(id) {
    set((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
    markDirty();
  },

  reorder(id, toIndex) {
    set((s) => {
      const from = s.entries.findIndex((e) => e.id === id);
      if (from < 0) return s;
      const entries = [...s.entries];
      const [moved] = entries.splice(from, 1);
      entries.splice(Math.max(0, Math.min(toIndex, entries.length)), 0, moved);
      return { entries };
    });
    markDirty();
  },

  async saveNow() {
    const { projectId, entries } = get();
    if (projectId === null) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    set({ saveState: 'saving' });
    try {
      await backend.projects.patch(projectId, { bin: entries });
      set((s) => ({
        saveState: s.saveState === 'saving' ? 'saved' : s.saveState,
      }));
    } catch {
      set({ saveState: 'error' });
    }
  },
}));

/**
 * Add an entry, deduplicating: an existing entry for the same archive+item with
 * the same marks (or both unmarked) is refreshed in place instead of duplicated
 * (its note and position survive). Returns the entry id.
 */
export function saveToBin(entry: Omit<BinEntry, 'id' | 'addedAt'>): string {
  const { entries, add, update } = useBin.getState();
  const sameMark = (a: number | undefined, b: number | undefined) =>
    a === undefined || b === undefined ? a === b : Math.abs(a - b) < 0.01;
  const existing = entries.find(
    (e) =>
      e.archive === entry.archive &&
      e.item === entry.item &&
      sameMark(e.inPoint, entry.inPoint) &&
      sameMark(e.outPoint, entry.outPoint),
  );
  if (existing) {
    update(existing.id, {
      title: entry.title,
      duration: entry.duration,
      parts: entry.parts,
      posterFrame: entry.posterFrame,
      inPoint: entry.inPoint,
      outPoint: entry.outPoint,
    });
    return existing.id;
  }
  return add(entry);
}

function markDirty() {
  if (useBin.getState().projectId === null) return;
  useBin.setState({ saveState: 'dirty' });
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    void useBin.getState().saveNow();
  }, AUTOSAVE_MS);
}
